import {
  IonContent,
  IonFab,
  IonFabButton,
  IonFabList,
  IonHeader,
  IonIcon,
  IonItem,
  IonNote,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillLeave,
} from "@ionic/react";
import React, { useContext, useEffect, useState, useRef } from "react";
import Decimal from "decimal.js";
import { send } from "../../../utils/sockets/asyncSocket";
import config from "../../../components/helpers/socket/config";
import { v4 as uuidv4 } from "uuid";
import PositionsListItem from "./PositionsListItem";
import { pubsubEvents } from "../../../components/helpers/socket/pubsubEvent";
import Skeleton__Positions from "./Skeleton__Positions";
import { hapticsImpactMedium } from "../../../utils/core-utils/hapticUtils";
import {
  ellipsisHorizontal,
  chevronExpandOutline,
  walletOutline,
} from "ionicons/icons";
import AggPositionsAccordion from "./AggPositionsAccordion";
import UserPrefencesContext, {
  UserPreferences,
} from "./../../../components/contexts/UserPreferencesContext";
import { saveUserPreferences } from "../../../utils/core-utils/preferenceUtils";
import firebaseEventConfig from "../../../utils/firebaseAnalytics/firebaseEventConfig";
import { CMEDfirebaseLogEvent } from "../../../utils/firebaseAnalytics/firebaseUtils";
import AppPrefContext from "../../../components/contexts/AppPrefContext";
import {
  MetricConstants,
  MetricTypeConstants,
} from "../../../components/Constants";
import {
  startMetric,
  endMetric,
} from "../../../utils/metric-utils/metricUtils";
import TotalPnlRow from "./TotalPnlRow";

/**
 * @author
 * E45597 Aryamitra Chaudhuri
 */

const monthMap: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const extractDate = (str: string): Date => {
  const match = str
    ?.toLowerCase()
    ?.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*?(\d{2})/);
  if (match) {
    const month = monthMap[match[1]];
    const year = parseInt("20" + match[2]);
    return new Date(year, month);
  }
  return new Date(9999, 0);
};

const Positions: React.FC = () => {
  const [positionsState, setPositionsState] = useState<{
    flat: any[];
    structured: any[];
  }>({
    flat: [],
    structured: [],
  });

  const [positionsLoading, setPositionsLoading] = useState<boolean>(true);
  const [expandedItems, setExpandedItems] = useState<(string | number)[]>([]);
  const [fabButtonOptions, setFabButtonOptions] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const appPref = useContext(AppPrefContext);

  const { userPreferences, setUserPreferences } =
    useContext(UserPrefencesContext);

  const buildStructuredPositions = (flatData: any[]) => {
    if (!flatData || flatData?.length === 0) return [];

    const processedData = flatData.map((item: any) => {
      const pc_d = new Decimal(item?.pc?.r ?? 0);
      const g_d = new Decimal(item?.g?.r ?? 0);
      const gs_d = new Decimal(item?.gs?.r ?? 0);
      const l_d = new Decimal(item?.l?.r ?? 0);
      const a_d = new Decimal(item?.a?.r ?? 0);
      const as_d = new Decimal(item?.as?.r ?? 0);

      const netPos = g_d.minus(gs_d);
      const pnl = pc_d.times(g_d.minus(gs_d).times(l_d).minus(a_d).plus(as_d));

      return { ...item, netPos, pnl };
    });

    const parents = processedData
      ?.filter((o: any) => o?.p === null)
      ?.sort((a: any, b: any) => a?.d?.localeCompare(b?.d));

    return parents?.map((parent: any) => {
      const children = processedData
        ?.filter((child: any) => child?.p === parent?.id)
        ?.sort((a: any, b: any) => {
          const dateA = extractDate(a?.d ?? "");
          const dateB = extractDate(b?.d ?? "");
          return dateA?.getTime() - dateB?.getTime();
        });

      let parentPnl = parent.pnl;
      if (children && children.length > 0) {
        parentPnl = children.reduce((acc: Decimal, child: any) => {
          return acc.plus(child.pnl);
        }, new Decimal(0));
      }

      return {
        ...parent,
        pnl: parentPnl,
        children,
      };
    });
  };

  const getResForTradingPositionAggregates = (msg: any, response: any) => {
    const { data, isSnapshot } = response?.payload;
    switch (isSnapshot) {
      case true:
        setPositionsState(() => {
          const structured = buildStructuredPositions(data);
          return { flat: data, structured };
        });
        timerRef.current = setTimeout(() => {
          setPositionsLoading(false);
        }, 3000);
        endMetric(MetricConstants.POSITIONS_LOAD_LATENCY);
        break;

      case null:
        setPositionsState((prevState) => {
          const updatedData = [...prevState.flat];
          if (Array?.isArray(data)) {
            data?.forEach((newItem) => {
              if (newItem && newItem?.id) {
                const index = updatedData?.findIndex(
                  (item) => item && item?.id === newItem?.id,
                );
                if (index !== -1) {
                  updatedData[index] = newItem;
                } else {
                  updatedData?.push(newItem);
                }
              }
            });
          }
          const newFlat = updatedData?.filter(Boolean);
          const newStructured = buildStructuredPositions(newFlat);

          return { flat: newFlat, structured: newStructured };
        });

        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        setPositionsLoading(false);
        break;
      default:
        break;
    }
  };

  let request = {
    action: "cmed/orders/subscribe",
    type: "ordersDataSubscribe",
    requestID: uuidv4(),
    payload: {
      ordersDataType: "TradingPositionAggregates",
    },
  };

  useEffect(() => {
    if (!appPref?.isAppActive) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setPositionsState({ flat: [], structured: [] });
      setPositionsLoading(true);
    }
  }, [appPref?.isAppActive]);

  useIonViewWillLeave(() => {
    setFabButtonOptions(false);
  });

  useEffect(() => {
    PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.CMED_POSITION_AGGREGATES_UPDATE,
      getResForTradingPositionAggregates,
    );
    startMetric(
      MetricConstants.POSITIONS_LOAD_LATENCY,
      MetricTypeConstants.TIMER,
    );
    send(config.socket.channels.TRADING, request);
  }, []);

  const PositionsHeader = () => {
    if (userPreferences.aggregatedview) {
      return (
        <IonItem
          className="ion-no-padding"
          lines="none"
          style={{
            "--inner-padding-end": 0,
            "--min-height": "0px",
            "--background": "none",
            marginTop: "2px",
          }}
        >
          <div className="pg__header_agg_view_parent">
            <div
              className="pg__contract_net_pos_header"
              style={{ marginLeft: "5px" }}
            >
              CONTRACT <br /> NET POS
            </div>
            <div className="pg__buyqty_sellqty_header">
              BUY QTY
              <br />
              SELL QTY
            </div>
            <div className="pg__avgbuy_avgsell_header">
              AVG BUY
              <br />
              AVG SELL
            </div>
            <div
              className="pg__refprice_pnl_header"
              style={{ marginRight: "30px" }}
            >
              REF PRICE
              <br />
              P&L (USD)
            </div>
          </div>
        </IonItem>
      );
    }

    return (
      <IonItem
        className="ion-no-padding"
        lines="none"
        style={{
          "--inner-padding-end": 0,
          "--min-height": "0px",
          "--background": "none",
          marginTop: "2px",
        }}
      >
        <div className="pg__header_parent">
          <div className="pg__contract_net_pos_header">
            CONTRACT <br /> NET POS
          </div>
          <div className="pg__buyqty_sellqty_header">
            BUY QTY
            <br />
            SELL QTY
          </div>
          <div className="pg__avgbuy_avgsell_header">
            AVG BUY
            <br />
            AVG SELL
          </div>
          <div className="pg__refprice_pnl_header">
            REF PRICE
            <br />
            P&L (USD)
          </div>
        </div>
      </IonItem>
    );
  };

  const handleToggleAggregatedView = () => {
    hapticsImpactMedium();
    const nextAggViewValue = !userPreferences.aggregatedview;

    const updatedPreferences: UserPreferences = {
      ...userPreferences,
      aggregatedview: nextAggViewValue,
    };

    setUserPreferences(updatedPreferences);
    saveUserPreferences(updatedPreferences);

    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_POSITIONS,
      firebaseEventConfig.eventNames.CMED_POSITIONS
        .CMED_POSITIONS_AGGREGATED_VIEW,
    );
    setFabButtonOptions(!fabButtonOptions);
  };

  const handleToggleTotalPnl = () => {
    hapticsImpactMedium();
    const nextPnlViewValue = !userPreferences.pnlview;

    const updatedPreferences: UserPreferences = {
      ...userPreferences,
      pnlview: nextPnlViewValue,
    };

    setUserPreferences(updatedPreferences);
    saveUserPreferences(updatedPreferences);

    setFabButtonOptions(!fabButtonOptions);
  };

  const { structured: structuredPositions } = positionsState;

  const totalPnl = structuredPositions.reduce((acc: Decimal, parent: any) => {
    return acc.plus(parent.pnl);
  }, new Decimal(0));

  if (positionsLoading) {
    return (
      <IonPage>
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle></IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Skeleton__Positions />
        </IonContent>
      </IonPage>
    );
  }

  if (!structuredPositions || structuredPositions.length === 0) {
    return (
      <IonPage>
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle></IonTitle>
          </IonToolbar>
        </IonHeader>
        <PositionsHeader />
        <IonContent>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <IonNote
              color="medium"
              style={{ fontSize: "var(--one-font-size-10)" }}
            >
              No Positions Found
            </IonNote>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  let contentList;
  if (userPreferences.aggregatedview) {
    contentList = (
      <div style={{ margin: "10px" }}>
        <AggPositionsAccordion
          structuredPositions={structuredPositions}
          expandedItems={expandedItems}
          setExpandedItems={setExpandedItems}
        />
      </div>
    );
  } else {
    contentList = structuredPositions
      ?.flatMap((parent: any) => parent?.children)
      ?.map((elem: any, index: number) => (
        <PositionsListItem data={elem} key={index} />
      ));
  }

  let fabIconColor = "";
  if (userPreferences.aggregatedview) {
    fabIconColor = "success";
  }

  let totalPnlFabIconColor = "";
  if (userPreferences.pnlview) {
    totalPnlFabIconColor = "success";
  }

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle></IonTitle>
        </IonToolbar>
      </IonHeader>
      <PositionsHeader />
      <IonContent>
        {userPreferences.pnlview && (
          <TotalPnlRow
            totalPnl={totalPnl}
            aggregatedview={userPreferences.aggregatedview}
            pnlview={userPreferences.pnlview}
          />
        )}
        {contentList}
      </IonContent>
      <IonFab
        activated={fabButtonOptions}
        slot="fixed"
        vertical="bottom"
        horizontal="end"
      >
        <IonFabButton
          color={"secondary"}
          size="small"
          style={{ opacity: 0.8 }}
          onClick={() => setFabButtonOptions(!fabButtonOptions)}
        >
          <IonIcon icon={ellipsisHorizontal}></IonIcon>
        </IonFabButton>
        <IonFabList side="top">
          <IonFabButton
            title="Aggregated View"
            onClick={handleToggleAggregatedView}
          >
            <IonIcon icon={chevronExpandOutline} color={fabIconColor}></IonIcon>
          </IonFabButton>
          <IonFabButton title="Total P&L" onClick={handleToggleTotalPnl}>
            <IonIcon
              icon={walletOutline}
              color={totalPnlFabIconColor}
            ></IonIcon>
          </IonFabButton>
        </IonFabList>
      </IonFab>
    </IonPage>
  );
};

export default Positions;
